import {ControlType} from './control-type.enum';
import {DiagnosisListLayout} from './diagnosis-list-layout.enum';
import {WidthType} from './width-type.enum';
import {JumpTextEntity} from '../../report-renderer/entities/jump-text-entity';
import {Medication} from '../../repositories/medication-repository/entities/medication';
import {Problem} from '../../repositories/problems-repository/entities/problem';
import {TreatmentPlanItem} from '../../repositories/treatment-plans-repository/entities/treatment-plan-item';
import {Patient} from '../../repositories/patient-repository/entities/patient';
import {BillingDiagnosisCode} from '../../repositories/patient-billing-repository/entities/billing-diagnosis-code';
import {BillingEntryLineItem} from '../../repositories/patient-billing-repository/entities/billing-entry-line-item';
import {PatientDiagnosisCode} from '../../repositories/diagnosis-codes-repository/entities/patient-diagnosis-code';
import {ScriptType} from '../../repositories/scripts-repository/entities/script-type';

export interface IReportControl {
  type: ControlType;
  width: WidthType;
  label: string;
  placeHolder: string;
  units: string;
  value: string;
  jumpTarget: string;
  inline: boolean;
  isEditing: boolean;
  showTitleDate: boolean;
  titleDateValue: string;
  displayContactDetails: boolean;
  displayAddressDetails: boolean;
  displayMedicalAidDetails: boolean;
  displayPracticeDetails: boolean;
  displayDoctorsDetails: boolean;
  procedureId: number;
  procedureField: number;
  medicationListValue: Medication[];
  problemListValue: Problem[];
  treatmentPlanListValue: TreatmentPlanItem[];
  diagnosisListValue: PatientDiagnosisCode[];
  patientValue: Patient;
  /**
   * Restricts a MEDICATION_LIST control to a single script type.
   *
   * Null means every script type. That is what medication controls did before this filter existed,
   * so a control saved without a value has to keep printing the whole list — do not treat a missing
   * value as "no script types".
   */
  scriptType: ScriptType | null;
  /**
   * Whether a MEDICATION_LIST control prints each item's diagnosis code alongside its description.
   * Unset means true — printing the code is what medication controls did before this toggle existed.
   */
  displayMedicationDiagnosisCode: boolean;
  /**
   * Whether a DIAGNOSIS_LIST control prints each code's description alongside the code itself.
   * Unset means true, for the same reason.
   *
   * Has no effect while diagnosisLayout is HORIZONTAL — that layout prints codes only.
   */
  displayDiagnosisDescription: boolean;
  /**
   * How a DIAGNOSIS_LIST control arranges its entries. VERTICAL is the pre-existing stacked layout and is
   * enum value 0, so a control saved before this existed reads back as it has always printed.
   */
  diagnosisLayout: DiagnosisListLayout;
  billingIcd10ListValue: BillingDiagnosisCode[];
  billingLineItemsValue: BillingEntryLineItem[];
  displayCodeDetails: boolean;
  displayMultiColumn: boolean;
  bold: boolean;
  labelColour: string;
  valueColour: string;
  stickerWidth: number | null;
  stickerHeight: number | null;
  imageUploadId: number | null;
  imageBase64Data: string | null;
  imageContentType: string | null;
}


export class ReportControl implements IReportControl {
    type: ControlType = ControlType.SINGLE_LINE_TEXT_FIELD;
    width: WidthType = WidthType.AUTO;
    label = '';
    placeHolder = '';
    units = '';
    value = '';
    jumpTarget = '#';
    inline = false;
    isEditing = false;
    showTitleDate = false;
    titleDateValue = '';
    displayContactDetails = false;
    displayAddressDetails = false;
    displayMedicalAidDetails = false;
    displayPracticeDetails = false;
    displayDoctorsDetails = false;
    medicationListValue = [];
    problemListValue = [];
    treatmentPlanListValue = [];
    diagnosisListValue: PatientDiagnosisCode[] = [];
    patientValue = null;
    scriptType: ScriptType | null = null;
    displayMedicationDiagnosisCode = true;
    displayDiagnosisDescription = true;
    diagnosisLayout: DiagnosisListLayout = DiagnosisListLayout.VERTICAL;
    billingIcd10ListValue: BillingDiagnosisCode[] = [];
    billingLineItemsValue: BillingEntryLineItem[] = [];
    // False, matching what billing controls have always actually printed. The server never carried this
    // property through its save model, so it has been false on every stored template regardless of what
    // the checkbox showed. Defaulting to true here would make newly placed controls start printing code
    // descriptions the moment the server begins accepting the field.
    displayCodeDetails = false;
    displayMultiColumn = false;
    bold = false;
    procedureId: number;
    procedureField: number;
    labelColour = '#000000';
    valueColour = '#000000';
    stickerWidth: number | null = 100;
    stickerHeight: number | null = 60;
    imageUploadId: number | null = null;
    imageBase64Data: string | null = null;
    imageContentType: string | null = null;

    get jumpTextTemplate(): JumpTextEntity {
      return {
        text: this.value,
        jumpTarget: this.jumpTarget,
        replaceList: ''
      } as JumpTextEntity;
    }

    setWidthTypeFromIndex(index: any) {
        index = parseInt(index, 10);
        switch (index) {
            case 0:
                this.width = WidthType.AUTO;
                break;
            case 1:
                this.width = WidthType.FULL;
                break;
            case 2:
                this.width = WidthType.THREE_QUARTERS;
                break;
            case 3:
                this.width = WidthType.TWO_THIRDS;
                break;
            case 4:
                this.width = WidthType.HALF;
                break;
            case 5:
                this.width = WidthType.ONE_THIRD;
                break;
            case 6:
                this.width = WidthType.ONE_QUARTER;
                break;
            default:
                this.width = WidthType.AUTO;
                break;
        }
    }

    get showDate(): boolean {
        return this.type === ControlType.SECTION_TITLE && this.showTitleDate;
    }

    get hasLabel(): boolean {
        return this.label.length > 0;
    }

    get hasUnits(): boolean {
        return this.units.length > 0;
    }

    get hasPlaceholder(): boolean {
        return this.placeHolder.length > 0;
    }

    get widthChanged(): boolean {
        return this.width !== WidthType.AUTO;
    }

    get isSectionTitle(): boolean {
        return this.type ===  ControlType.SECTION_TITLE;
    }

    get isDatePicker(): boolean {
        return this.type === ControlType.DATE_PICKER;
    }

    get isSingleLineTextField(): boolean {
        return this.type === ControlType.SINGLE_LINE_TEXT_FIELD;
    }

    get isMultiLineTextField(): boolean {
        return this.type === ControlType.MULTILINE_TEXT_FIELD;
    }

    get isJumpTextField(): boolean {
        return this.type === ControlType.JUMP_TEXT_FIELD;
    }

    get isPatientSummary(): boolean {
        return this.type === ControlType.PATIENT_SUMMARY;
    }

    get isLatestComment(): boolean {
        return this.type === ControlType.LATEST_COMMENT;
    }

    get isProblemList(): boolean {
        return this.type === ControlType.PROBLEM_LIST;
    }

    get isMedicationList(): boolean {
        return this.type === ControlType.MEDICATION_LIST;
    }

    get isDiagnosisList(): boolean {
        return this.type === ControlType.DIAGNOSIS_LIST;
    }

    get isTreatmentPlanList(): boolean {
      return this.type === ControlType.TREATMENT_PLAN_LIST;
    }

    get isSignatureField(): boolean {
      return this.type === ControlType.SIGNATURE_FIELD;
    }

    get isExtendedSignatureField(): boolean {
      return this.type === ControlType.EXTENDED_SIGNATURE_FIELD;
    }

    get isProcedureField(): boolean {
      return this.type === ControlType.PROCEDURE_FIELD;
    }

    get isBillingIcd10List(): boolean {
      return this.type === ControlType.BILLING_ICD10_LIST;
    }

    get isBillingLineItems(): boolean {
      return this.type === ControlType.BILLING_LINE_ITEMS;
    }

    get isStickerField(): boolean {
      return this.type === ControlType.STICKER_FIELD;
    }

    get isImageField(): boolean {
      return this.type === ControlType.IMAGE_FIELD;
    }

    get widthClass(): string {
        switch (this.width) {
            case WidthType.AUTO:
                return 'col-sm';
            case WidthType.FULL:
                return 'col-sm-12';
            case WidthType.THREE_QUARTERS:
                return 'col-sm-9';
            case WidthType.TWO_THIRDS:
                return 'col-sm-8';
            case WidthType.HALF:
                return 'col-sm-6';
            case WidthType.ONE_THIRD:
                return 'col-sm-4';
            case WidthType.ONE_QUARTER:
                return 'col-sm-3';
            default:
                return 'col-sm-12';
        }
    }

    get title(): string {
        switch (this.type) {
            case ControlType.SECTION_TITLE:
                return 'Section Title';
            case ControlType.DATE_PICKER:
                return 'Date Picker';
            case ControlType.PATIENT_SUMMARY:
                return 'Patient Summary';
            case ControlType.SINGLE_LINE_TEXT_FIELD:
                return 'Text Field';
            case ControlType.MULTILINE_TEXT_FIELD:
                return 'Multiline Text Field';
            case ControlType.JUMP_TEXT_FIELD:
                return 'Jump Text Field';
            case ControlType.LATEST_COMMENT:
                return 'Latest Comment';
            case ControlType.MEDICATION_LIST:
                return 'Medication List';
            case ControlType.PROBLEM_LIST:
                return 'Problem List';
            case ControlType.DIAGNOSIS_LIST:
                return 'Diagnosis Code List';
          case ControlType.TREATMENT_PLAN_LIST:
            return 'Treatment Plan List';
          case ControlType.PROCEDURE_FIELD:
            return 'Procedure Field';
          case ControlType.SIGNATURE_FIELD:
            return 'Signature Block';
          case ControlType.EXTENDED_SIGNATURE_FIELD:
            return 'Extended Signature';
          case ControlType.BILLING_ICD10_LIST:
            return 'Billing ICD-10 List';
          case ControlType.BILLING_LINE_ITEMS:
            return 'Billing Line Items';
          case ControlType.STICKER_FIELD:
            return 'Sticker Field';
          case ControlType.IMAGE_FIELD:
            return 'Image Field';
            default:
                return 'Single Line Text Field';
        }
    }
}

export class ReportControlBuilder {

    static initializeNewControl(type: ControlType): ReportControl {
        const control = new ReportControl();
        control.type = type;
        control.label = control.title;
        switch (control.type) {
            case ControlType.PATIENT_SUMMARY:
                control.width = WidthType.FULL;
                control.displayContactDetails = true;
                control.displayAddressDetails = true;
                control.displayMedicalAidDetails = true;
                control.displayPracticeDetails = true;
                control.displayDoctorsDetails = true;
                break;
            case ControlType.SIGNATURE_FIELD:
                control.width = WidthType.FULL;
                control.bold = true;
                control.value = 'Dr Name Doctor';
                break;
            case ControlType.EXTENDED_SIGNATURE_FIELD:
                control.width = WidthType.FULL;
                control.bold = true;
                control.value = 'Dr Name Doctor\nDr Other Doctor\nMP 1234567 MbChB';
                break;
            case ControlType.BILLING_ICD10_LIST:
                control.displayCodeDetails = false;
                break;
            case ControlType.BILLING_LINE_ITEMS:
                control.displayCodeDetails = false;
                control.displayMultiColumn = false;
                break;
            case ControlType.STICKER_FIELD:
                control.stickerWidth = 100;
                control.stickerHeight = 60;
                break;
            default:
                control.width = WidthType.AUTO;
                break;
        }
        return control;
    }

    static fromJson(jsonObject: IReportControl): ReportControl {
        const control = new ReportControl();
        control.type = jsonObject?.type ?? ControlType.SINGLE_LINE_TEXT_FIELD;
        control.width = jsonObject?.width ?? WidthType.AUTO;
        control.label = jsonObject?.label ?? '';
        control.value = jsonObject?.value ?? '';
        control.units = jsonObject?.units ?? '';
        control.placeHolder = jsonObject?.placeHolder ?? '';
        control.jumpTarget = jsonObject?.jumpTarget ?? '#';
        control.inline = jsonObject?.inline ?? false;
        control.isEditing = jsonObject?.isEditing ?? false;
        control.showTitleDate = jsonObject?.showTitleDate ?? false;
        control.titleDateValue = jsonObject?.titleDateValue ?? '';
        control.displayContactDetails = jsonObject?.displayContactDetails ?? false;
        control.displayAddressDetails = jsonObject?.displayAddressDetails ?? false;
        control.displayMedicalAidDetails = jsonObject?.displayMedicalAidDetails ?? false;
        control.displayPracticeDetails = jsonObject?.displayPracticeDetails ?? false;
        control.displayDoctorsDetails = jsonObject?.displayDoctorsDetails ?? false;
        control.treatmentPlanListValue = jsonObject?.treatmentPlanListValue;
        control.medicationListValue = jsonObject?.medicationListValue;
        control.problemListValue = jsonObject?.problemListValue;
        control.diagnosisListValue = jsonObject?.diagnosisListValue ?? [];
        control.patientValue = jsonObject?.patientValue;
        // ?? keeps ScriptType.ACUTE (0) intact — only null/undefined fall through to "all script types".
        control.scriptType = jsonObject?.scriptType ?? null;
        control.displayMedicationDiagnosisCode = jsonObject?.displayMedicationDiagnosisCode ?? true;
        control.displayDiagnosisDescription = jsonObject?.displayDiagnosisDescription ?? true;
        // ?? keeps VERTICAL (0) intact and makes an absent value mean the stacked layout, which is what
        // every already-saved diagnosis control has printed.
        control.diagnosisLayout = jsonObject?.diagnosisLayout ?? DiagnosisListLayout.VERTICAL;
        control.billingIcd10ListValue = jsonObject?.billingIcd10ListValue ?? [];
        control.billingLineItemsValue = jsonObject?.billingLineItemsValue ?? [];
        // ?? false, not ?? true: a stored template that predates the server carrying this property has no
        // value for it, and it has never printed code descriptions. Reading it as true would show the
        // checkbox ticked for output that has never included descriptions.
        control.displayCodeDetails = jsonObject?.displayCodeDetails ?? false;
        control.displayMultiColumn = jsonObject?.displayMultiColumn ?? false;
        control.bold = jsonObject?.bold ?? false;
        control.procedureId = jsonObject?.procedureId ?? 0;
        control.procedureField = jsonObject?.procedureField ?? 0;
        control.labelColour = jsonObject?.labelColour ?? '#000000';
        control.valueColour = jsonObject?.valueColour ?? '#000000';
        control.stickerWidth = jsonObject?.stickerWidth ?? null;
        control.stickerHeight = jsonObject?.stickerHeight ?? null;
        control.imageUploadId = jsonObject?.imageUploadId ?? null;
        control.imageBase64Data = jsonObject?.imageBase64Data ?? null;
        control.imageContentType = jsonObject?.imageContentType ?? null;
        return control;
    }
}
